import { RenderHTML } from '@wix/cc-user-facing-common-components';
import { Notification } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useState } from 'react';

import { AI_TRANSLATED_LABEL_ID, AI_TRANSLATED_RU_ARTICLE_IDS } from './consts';
import { LOCALES } from '../../../constants';
import { DATA_HOOKS } from '../../../dataHooks';
import { PageType } from '../../../types';
import { useStore } from '../../../zustand/stores';

type ArticleLabel = { id?: string };

type AiTranslatedNotificationProps = {
  pageType: PageType;
};

const hasAiTranslatedLabel = (labels?: ArticleLabel[]): boolean =>
  !!labels?.some((label) => label.id === AI_TRANSLATED_LABEL_ID);

const shouldShowAiTranslatedNotification =
  (locale: string | undefined, pageType: PageType, itemId?: string, articleLabels?: ArticleLabel[]): boolean => {
    const isHomepage = pageType === PageType.Homepage;
    const isArticle = pageType === PageType.Article;

    if (!isHomepage && !isArticle) {
      return false;
    }

    if (isArticle && hasAiTranslatedLabel(articleLabels)) {
      return true;
    }

    if (locale === LOCALES.ZH_HANT || locale === LOCALES.HI) {
      return true;
    }

    if (locale === LOCALES.RU && isArticle && itemId) {
      return AI_TRANSLATED_RU_ARTICLE_IDS.has(itemId);
    }

    return false;
  };

export const AiTranslatedNotification: FunctionComponent<AiTranslatedNotificationProps> =
  ({ pageType }) => {
    const { t } = useTranslation();
    const { locale } = useRouter();
    const itemId = useStore((state) => state.pageSlice.itemId);
    const articleLabels = useStore((state) => state.articleSlice.article?.labels);

    const isHomepage = pageType === PageType.Homepage;
    const shouldShow = shouldShowAiTranslatedNotification(locale, pageType, itemId, articleLabels);

    const [isDisplay, setIsDisplay] = useState(shouldShow);

    if (!shouldShow) {
      return null;
    }

    const notificationText = isHomepage
      ? t('notification.bar.homepage.ai-translated')
      : t('notification.bar.articlepage.ai-translated');

    return (
      <Notification
        dataHook={DATA_HOOKS.AI_TRANSLATED_NOTIFICATION}
        show={isDisplay}
        theme="standard"
        onClose={() => setIsDisplay(false)}
      >
        <Notification.TextLabel ellipsis={false} textAlign="center">
          <RenderHTML html={notificationText} />
        </Notification.TextLabel>
        <Notification.CloseButton />
      </Notification>
    );
  };
